import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Extend Window interface for Chatwoot
declare global {
  interface Window {
    chatwootSettings?: {
      hideMessageBubble?: boolean;
      position?: 'left' | 'right';
      locale?: string;
      type?: 'standard' | 'expanded_bubble';
      darkMode?: 'light' | 'auto';
    };
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (userId: string, userData: { email?: string; name?: string }) => void;
      reset: () => void;
      toggle: (state?: 'open' | 'close') => void;
      setCustomAttributes: (attributes: Record<string, string | number | boolean>) => void;
    };
  }
}

const CHATWOOT_BASE_URL = 'https://app.chatwoot.com';

export const ChatwootWidget = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const websiteToken = 'qxw65qKhCX8qYFFDhzvvuYB8';

    // Configure Chatwoot settings before loading the script
    window.chatwootSettings = {
      hideMessageBubble: false,
      position: 'right',
      locale: 'en',
      type: 'standard',
      darkMode: 'auto',
    };

    // Check if script is already loaded
    if (document.getElementById('chatwoot-sdk')) {
      setIsLoaded(true);
      return;
    }

    // Load Chatwoot SDK
    const script = document.createElement('script');
    script.id = 'chatwoot-sdk';
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken,
          baseUrl: CHATWOOT_BASE_URL,
        });
        setIsLoaded(true);
      }
    };

    script.onerror = () => {
      console.error('Chatwoot: Failed to load SDK');
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount (though typically this component stays mounted)
      const existingScript = document.getElementById('chatwoot-sdk');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Identify user when logged in
  useEffect(() => {
    if (!isLoaded) return;

    const identifyUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user && window.$chatwoot) {
        // Fetch profile for name
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', session.user.id)
          .single();

        window.$chatwoot.setUser(session.user.id, {
          email: session.user.email,
          name: profile?.full_name || undefined,
        });
      }
    };

    identifyUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user && window.$chatwoot) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', session.user.id)
          .single();

        window.$chatwoot.setUser(session.user.id, {
          email: session.user.email,
          name: profile?.full_name || undefined,
        });
      } else if (event === 'SIGNED_OUT' && window.$chatwoot) {
        window.$chatwoot.reset();
      }
    });

    return () => subscription.unsubscribe();
  }, [isLoaded]);

  // This component doesn't render anything visible
  return null;
};
